/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { useConnection } from "@solana/wallet-adapter-react";
import type { Connection, TransactionInstruction } from "@solana/web3.js";
import { GLOBAL_POLIS_LOCKER_ADDRESS_BASE, POLIS_MINT } from "@stepfinance/common";
import type { StarAtlasPolisLockPosition } from "@stepfinance/staking";
import {
  findEscrowAddress,
  findEscrowHistoryAddress,
  findLockerHistoryAddress,
  findProxyAddress,
  findProxyEscrowAddress,
  findRegisteredLockerAddress,
  findTreasuryAuthority,
  findTreasuryTokenAccount,
  getStarAtlasDate,
  makeProxyRewarderProgram,
} from "@stepfinance/staking";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { findOrCreateAccountByMint } from "../../../actions/account";
import { TokenChangeNotificationDisplay } from "../../../components/Notifications/TokenChangeNotificationDisplay";
import { useLanguageContext } from "../../../contexts/language/language";
import type { CommonNotificationsStrings } from "../../../contexts/language/languageTypes";
import { notify } from "../../../utils/notifications";
import { handlePreSendTxnError } from "../../../utils/transactions";
import { formatStringWithPlaceholders } from "../../../utils/utils";
import type { ConnectedWallet } from "../..";
import { useConnectedWallet } from "../..";

export const useClaimPolisLockerRewards = () => {
  const queryClient = useQueryClient();
  const { connection } = useConnection();
  const wallet = useConnectedWallet();
  const {
    currentLanguageData: { common },
  } = useLanguageContext();

  return useMutation({
    mutationFn: async (stake: StarAtlasPolisLockPosition) => {
      await claimPolisLockerRewards(connection, wallet!, common.notifications);
      return stake;
    },
    onSuccess: (stake) => {
      notify({
        message: formatStringWithPlaceholders(
          common.notifications.youDidAction.message,
          common.notifications.youDidAction.claimed,
          "POLIS",
        ),
        description: (
          <>
            <span>
              {formatStringWithPlaceholders(
                common.notifications.youDidAction.message,
                common.notifications.youDidAction.claimed,
              )}{" "}
            </span>
            <TokenChangeNotificationDisplay
              amount={stake.pendingReward!}
              mintAddress={stake.rewardMint}
              type="increase"
            />
          </>
        ),
        type: "success",
      });
      return queryClient.invalidateQueries({ queryKey: ["starAtlasLockers", wallet!.publicKey.toBase58()] });
    },
    onError: (error: Error) => handlePreSendTxnError(error),
  });
};

const claimPolisLockerRewards = async (
  connection: Connection,
  wallet: ConnectedWallet,
  notificationStrings: CommonNotificationsStrings,
): Promise<void> => {
  const proxyProgram = makeProxyRewarderProgram(connection, wallet);

  const now = getStarAtlasDate();
  const walletPublicKey = wallet.publicKey;
  const proxyEscrow = findProxyEscrowAddress(walletPublicKey);
  const proxy = findProxyAddress(proxyEscrow, walletPublicKey);
  const escrow = findEscrowAddress(GLOBAL_POLIS_LOCKER_ADDRESS_BASE, proxy);
  const registeredLocker = findRegisteredLockerAddress(GLOBAL_POLIS_LOCKER_ADDRESS_BASE);
  const treasuryTokenAccount = findTreasuryTokenAccount(GLOBAL_POLIS_LOCKER_ADDRESS_BASE);
  const treasuryAuthority = findTreasuryAuthority(GLOBAL_POLIS_LOCKER_ADDRESS_BASE);

  const lockerHistory = findLockerHistoryAddress(GLOBAL_POLIS_LOCKER_ADDRESS_BASE, now.era);
  const escrowHistory = findEscrowHistoryAddress(escrow, now.era);

  const instructions: TransactionInstruction[] = [];
  const userTokenAccount = await findOrCreateAccountByMint(
    connection,
    walletPublicKey,
    walletPublicKey,
    instructions,
    POLIS_MINT,
  );

  notify({
    message: notificationStrings.approveTransaction,
    type: "info",
  });

  await proxyProgram.methods
    .claimRewards()
    .preInstructions(instructions)
    .accounts({
      escrow,
      locker: GLOBAL_POLIS_LOCKER_ADDRESS_BASE,
      proxy,
      proxyEscrow,
      escrowHistory,
      lockerHistory,
      treasuryTokenAccount,
      registeredLocker,
      treasuryAuthority,
      userTokenAccount,
      escrowOwner: walletPublicKey,
      tokenProgram: TOKEN_PROGRAM_ID,
    })
    .rpc();
};
